import React, { useState, useRef, useEffect } from 'react';

const DateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [currentMonth, setCurrentMonth] = useState(new Date(startDate || new Date()));
  const [selectingStart, setSelectingStart] = useState(true);
  const [view, setView] = useState('calendar');
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isInRange = (date) => {
    if (!date || !tempStartDate || !tempEndDate) return false;
    const d = new Date(date);
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    return d >= start && d <= end;
  };

  const getSelectedWeekBounds = () => {
    if (!tempStartDate) return null;
    return getWeekBounds(new Date(tempStartDate));
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    if (!hasUserSelected || !tempStartDate) return false;
    
    if (selectingStart) {
      return false;
    } else {
      const weekBounds = getSelectedWeekBounds();
      if (!weekBounds) return false;
      const dateObj = new Date(date);
      const startDateObj = new Date(tempStartDate);
      return dateObj < startDateObj || !isDateInWeek(date, weekBounds.monday, weekBounds.sunday);
    }
  };

  const isStartDate = (date) => {
    if (!date || !tempStartDate) return false;
    return formatDateForInput(date) === tempStartDate;
  };

  const isEndDate = (date) => {
    if (!date || !tempEndDate) return false;
    return formatDateForInput(date) === tempEndDate;
  };

  const getWeekBounds = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { monday, sunday };
  };

  const isDateInWeek = (date, weekStart, weekEnd) => {
    if (!date) return false;
    const d = new Date(date);
    return d >= weekStart && d <= weekEnd;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const dateStr = formatDateForInput(date);
    
    if (selectingStart) {
      setTempStartDate(dateStr);
      setTempEndDate('');
      setHasUserSelected(true);
      setSelectingStart(false);
    } else {
      if (new Date(dateStr) < new Date(tempStartDate)) {
        setTempStartDate(dateStr);
        setTempEndDate('');
        setHasUserSelected(true);
        setSelectingStart(false);
      } else {
        const { monday, sunday } = getWeekBounds(new Date(tempStartDate));
        const weekEnd = formatDateForInput(sunday);
        const selectedEndDate = new Date(dateStr) <= new Date(weekEnd) ? dateStr : weekEnd;
        setTempEndDate(selectedEndDate);
        setSelectingStart(true);
        onDateChange(tempStartDate, selectedEndDate);
        setIsOpen(false);
      }
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToPreviousYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
  };

  const goToNextYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
  };

  const handleMonthYearClick = () => {
    setView('year');
  };

  const handleYearSelect = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setView('month');
  };

  const handleMonthSelect = (month) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), month, 1));
    setView('calendar');
  };

  const getYearRange = () => {
    const currentYear = currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12;
    const years = [];
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i);
    }
    return years;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setView('calendar');
          }
        }}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
      >
        <span className="text-gray-600">
          {formatDisplayDate(startDate)} to {formatDisplayDate(endDate)}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4 min-w-[300px]">
          {view === 'calendar' && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleMonthYearClick}
                  className="font-semibold text-gray-900 hover:text-[#78350F] transition-colors px-2 py-1 rounded"
                >
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </button>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Day names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(currentMonth).map((date, index) => {
                  const disabled = !date || (tempStartDate && isDateDisabled(date));
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDateClick(date)}
                      disabled={disabled}
                      className={`
                        w-9 h-9 text-sm rounded-full flex items-center justify-center transition-colors
                        ${!date ? 'invisible' : ''}
                        ${disabled && date ? 'opacity-30 cursor-not-allowed' : ''}
                        ${isStartDate(date) || isEndDate(date)
                          ? 'bg-[#78350F] text-white font-medium'
                          : isInRange(date)
                            ? 'bg-[#78350F]/10 text-[#78350F]'
                            : disabled && date
                              ? 'text-gray-400'
                              : 'hover:bg-gray-100 text-gray-700'}
                      `}
                    >
                      {date?.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Selection hint */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {selectingStart ? 'Select start date' : 'Select end date to apply'}
                </p>
                {!selectingStart && (
                  <button
                    type="button"
                    onClick={() => {
                      setTempStartDate(startDate);
                      setTempEndDate(endDate);
                      setSelectingStart(true);
                      setHasUserSelected(false);
                    }}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                    title="Reset selection"
                  >
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
              </div>
            </>
          )}

          {view === 'year' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousYear}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="font-semibold text-gray-900">
                  {Math.floor(currentMonth.getFullYear() / 12) * 12} - {Math.floor(currentMonth.getFullYear() / 12) * 12 + 11}
                </span>
                <button
                  type="button"
                  onClick={goToNextYear}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {getYearRange().map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    className={`
                      py-2 px-3 text-sm rounded-lg transition-colors
                      ${year === currentMonth.getFullYear()
                        ? 'bg-[#78350F] text-white font-medium'
                        : 'hover:bg-gray-100 text-gray-700'}
                    `}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}

          {view === 'month' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() => setView('year')}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="font-semibold text-gray-900">
                  {currentMonth.getFullYear()}
                </span>
                <div className="w-9"></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {monthNames.map((month, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleMonthSelect(index)}
                    className={`
                      py-2 px-3 text-sm rounded-lg transition-colors
                      ${index === currentMonth.getMonth()
                        ? 'bg-[#78350F] text-white font-medium'
                        : 'hover:bg-gray-100 text-gray-700'}
                    `}
                  >
                    {month.substring(0, 3)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
